import type { PluginListenerHandle } from '@capacitor/core';

export interface UrlEvent {
  /**
   * Webview instance id.
   */
  id?: string;
  /**
   * Emit when the url changes
   *
   * @since 0.0.1
   */
  url: string;
}
export interface BtnEvent {
  /**
   * Webview instance id.
   */
  id?: string;
  /**
   * Emit when a button is clicked.
   *
   * @since 0.0.1
   */
  url: string;
}

export type UrlChangeListener = (state: UrlEvent) => void;
export type ConfirmBtnListener = (state: BtnEvent) => void;
export type ButtonNearListener = (state: object) => void;

export enum BackgroundColor {
  WHITE = 'white',
  BLACK = 'black',
}
export enum ToolBarType {
  /**
   * Shows a simple toolbar with just a close button and share button
   * @since 0.1.0
   */
  ACTIVITY = 'activity',
  /**
   * Shows a simple toolbar with just a close button
   * @since 7.6.8
   */
  COMPACT = 'compact',
  /**
   * Shows a full navigation toolbar with back/forward buttons
   * @since 0.1.0
   */
  NAVIGATION = 'navigation',
  /**
   * Shows no toolbar
   * @since 0.1.0
   */
  BLANK = 'blank',
}

export enum InvisibilityMode {
  /**
   * WebView is aware it is hidden (dimensions may be zero).
   */
  AWARE = 'AWARE',
  /**
   * WebView is hidden but reports fullscreen dimensions (uses alpha=0 to remain invisible).
   */
  FAKE_VISIBLE = 'FAKE_VISIBLE',
}

export interface Headers {
  [key: string]: string;
}

export interface GetCookieOptions {
  url: string;
  includeHttpOnly?: boolean;
}

export interface ClearCookieOptions {
  /**
   * Target webview id.
   * When omitted, applies to all open webviews.
   */
  id?: string;
  url: string;
}

export interface Credentials {
  username: string;
  password: string;
}

/**
 * Represents an intercepted HTTP request from the in-app browser webview.
 *
 * Request and response bodies are base64-encoded when present.
 *
 * @since 8.6.0
 */
export interface ProxyRequest {
  requestId: string;
  phase: 'outbound' | 'inbound';
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string | null;
  status?: number;
  responseHeaders?: Record<string, string>;
  responseBody?: string | null;
  webviewId: string;
}

/**
 * Response payload returned to native for a proxied request.
 *
 * The body must be base64-encoded.
 *
 * @since 8.6.0
 */
export interface ProxyResponse {
  body: string;
  status: number;
  headers: Record<string, string>;
}

/**
 * Request override returned to native for an outbound proxied request.
 *
 * The body must be base64-encoded when present.
 *
 * @since 8.6.0
 */
export interface ProxyRequestOverride {
  url: string;
  method?: string;
  headers?: Record<string, string>;
  body?: string | null;
}

/**
 * Event emitted when a web page opens a popup/new window.
 *
 * @since 8.6.0
 */
export interface PopupWindowEvent {
  /**
   * Popup webview instance id.
   */
  id: string;
  /**
   * Parent webview instance id.
   */
  parentId?: string;
  /**
   * Requested popup URL when available.
   */
  url?: string;
  /**
   * Whether the popup was presented immediately.
   */
  visible: boolean;
}

/**
 * Event emitted when JavaScript running inside the managed webview writes to the console.
 *
 * Enable this with `captureConsoleLogs: true` when opening the webview.
 *
 * @since 8.6.0
 */
export interface ConsoleMessageEvent {
  /**
   * Source webview instance id.
   */
  id?: string;
  /**
   * Console method or normalized severity.
   */
  level: 'log' | 'info' | 'warn' | 'error' | 'debug' | 'assert' | string;
  /**
   * Joined string representation of the console arguments.
   */
  message: string;
  /**
   * Script URL or page URL when available.
   */
  source?: string;
  /**
   * 1-based line number when available.
   */
  line?: number;
  /**
   * 1-based column number when available.
   */
  column?: number;
  /**
   * Optional subtype for runtime-originated errors on supported platforms.
   */
  kind?: string;
}

/**
 * Native handling mode used after a managed download finishes.
 *
 * @since 8.6.0
 */
export type DownloadHandledBy = 'inAppBrowser' | 'systemPreview' | 'external';

/**
 * Event emitted after a managed download is saved locally.
 *
 * @since 8.6.0
 */
export interface DownloadCompletedEvent {
  /**
   * Source webview instance id.
   */
  id?: string;
  /**
   * Original URL that triggered the download when available.
   */
  sourceUrl?: string;
  /**
   * Saved filename.
   */
  fileName: string;
  /**
   * Resolved MIME type when available.
   */
  mimeType?: string;
  /**
   * Absolute native filesystem path to the saved file.
   */
  path: string;
  /**
   * `file://` URL pointing at the saved file.
   */
  localUrl: string;
  /**
   * How native handled the downloaded file after saving it.
   */
  handledBy: DownloadHandledBy;
}

/**
 * Event emitted when managed download handling fails.
 *
 * @since 8.6.0
 */
export interface DownloadFailedEvent {
  /**
   * Source webview instance id.
   */
  id?: string;
  /**
   * Original URL that triggered the download when available.
   */
  sourceUrl?: string;
  /**
   * Intended filename when known.
   */
  fileName?: string;
  /**
   * Resolved MIME type when available.
   */
  mimeType?: string;
  /**
   * Native error message.
   */
  error: string;
}

/**
 * Decision returned to native when handling a proxied request.
 *
 * @since 8.6.0
 */
export interface ProxyDecision {
  request?: ProxyRequestOverride | null;
  response?: ProxyResponse | null;
  cancel?: boolean;
}

/**
 * Native-first proxy rule used on Android and iOS.
 *
 * Any regex property that is omitted is treated as a wildcard.
 *
 * @since 8.6.0
 */
export interface NativeProxyRule {
  id?: string;
  urlRegex?: string;
  methodRegex?: string;
  headerRegex?: string;
  bodyRegex?: string;
  statusRegex?: string;
  responseHeaderRegex?: string;
  responseBodyRegex?: string;
  mainFrameOnly?: boolean;
  action: 'continue' | 'cancel' | 'delegateToJs';
}

export type ProxyHandlerResult = Response | ProxyResponse | ProxyRequestOverride | ProxyDecision | null;

/**
 * JavaScript callback used to handle proxied requests.
 *
 * Return a `Response`, `ProxyResponse`, `ProxyRequestOverride`, `ProxyDecision`,
 * or `null` to let native continue unchanged.
 *
 * @since 8.6.0
 */
export type ProxyHandler = (request: ProxyRequest) => ProxyHandlerResult | Promise<ProxyHandlerResult>;

export interface OpenOptions {
  /**
   * Target URL to load.
   * @since 0.1.0
   */
  url: string;
  /**
   * if true, the browser will be presented after the page is loaded, if false, the browser will be presented immediately.
   * @since 0.1.0
   */
  isPresentAfterPageLoad?: boolean;
  /**
   * if true the deeplink will not be opened, if false the deeplink will be opened when clicked on the link
   * @since 0.1.0
   */
  preventDeeplink?: boolean;

  // --- Chrome Custom Tab customization (Android only, ignored on iOS) ---

  /**
   * Toolbar background color in hex format (e.g., "#1A1A2E").
   * Applied to both light and dark color schemes.
   * Also sets the navigation bar color to match.
   * **Android only** — ignored on iOS.
   * @since 8.2.0
   */
  toolbarColor?: string;

  /**
   * Whether the URL bar should auto-hide when the user scrolls down.
   * The bar reappears on any upward scroll.
   * **Android only** — ignored on iOS.
   * @default false
   * @since 8.2.0
   */
  urlBarHidingEnabled?: boolean;

  /**
   * Show the page's HTML <title> in the toolbar instead of the raw URL.
   * The true URL is still visible when the user taps the title area.
   * **Android only** — ignored on iOS.
   * @default false
   * @since 8.2.0
   */
  showTitle?: boolean;

  /**
   * Replace the default "X" close icon with a back arrow.
   * Makes the Custom Tab feel like a native navigation push rather than a modal overlay.
   * **Android only** — ignored on iOS.
   * @default false
   * @since 8.2.0
   */
  showArrow?: boolean;

  /**
   * Remove the share action from the overflow menu.
   * **Android only** — ignored on iOS.
   * @default false
   * @since 8.2.0
   */
  disableShare?: boolean;

  /**
   * Hide the bookmark star icon in the overflow menu.
   * Uses an undocumented Chromium intent extra — may stop working on future Chrome updates.
   * **Android only** — ignored on iOS.
   * @default false
   * @since 8.2.0
   */
  disableBookmark?: boolean;

  /**
   * Hide the download icon in the overflow menu.
   * Uses an undocumented Chromium intent extra — may stop working on future Chrome updates.
   * **Android only** — ignored on iOS.
   * @default false
   * @since 8.2.0
   */
  disableDownload?: boolean;
}

export interface OpenSecureWindowOptions {
  /**
   * The endpoint to open
   */
  authEndpoint: string;
  /**
   * The redirect URI to use for the openSecureWindow call.
   * This will be checked to make sure it matches the redirect URI after the window finishes the redirection.
   */
  redirectUri: string;
  /**
   * The name of the broadcast channel to listen to, relevant only for web
   */
  broadcastChannelName?: string;
  /**
   * If true, the browser session will be ephemeral (no cookies or browsing data are shared with the system browser).
   * On iOS, this sets `prefersEphemeralWebBrowserSession = true` on `ASWebAuthenticationSession`.
   * On Android, ephemeral mode is always enabled via `FLAG_ACTIVITY_NO_HISTORY` regardless of this option.
   * @default false
   * @since 6.6.0
   */
  prefersEphemeralWebBrowserSession?: boolean;
}

export interface OpenSecureWindowResponse {
  /**
   * The result of the openSecureWindow call
   */
  redirectedUri: string;
}

export interface DisclaimerOptions {
  /**
   * Title of the disclaimer dialog
   * @default "Title"
   */
  title: string;
  /**
   * Message shown in the disclaimer dialog
   * @default "Message"
   */
  message: string;
  /**
   * Text for the confirm button
   * @default "Confirm"
   */
  confirmBtn: string;
  /**
   * Text for the cancel button
   * @default "Cancel"
   */
  cancelBtn: string;
}

export interface ScreenshotResult {
  /**
   * Image format used for the screenshot.
   */
  format: 'png';
  /**
   * MIME type of the generated screenshot.
   */
  mimeType: 'image/png';
  /**
   * Base64-encoded screenshot payload without the data URL prefix.
   */
  base64: string;
  /**
   * Data URL for direct use in HTML img tags or uploads.
   */
  dataUrl: string;
  /**
   * Screenshot width in pixels.
   */
  width: number;
  /**
   * Screenshot height in pixels.
   */
  height: number;
}

export interface CloseWebviewOptions {
  /**
   * Target webview id to close. If omitted, closes the active webview.
   */
  id?: string;
  /**
   * Whether the webview closing is animated or not, ios only
   * @default true
   */
  isAnimated?: boolean;
}

export interface OpenWebViewOptions {
  /**
   * Target URL to load.
   * @since 0.1.0
   * @example "https://capgo.app"
   */
  url: string;
  /**
   * Headers to send with the request.
   * @since 0.1.0
   * @example
   * headers: {
   *   "Custom-Header": "test-value",
   *   "Authorization": "Bearer test-token"
   * }
   * Test URL: https://www.whatismybrowser.com/detect/what-http-headers-is-my-browser-sending/
   */
  headers?: Headers;
  /**
   * Credentials to send with the request and all subsequent requests for the same host.
   * @since 6.1.0
   * @example
   * credentials: {
   *   username: "test-user",
   *   password: "test-pass"
   * }
   * Test URL: https://www.whatismybrowser.com/detect/what-http-headers-is-my-browser-sending/
   */
  credentials?: Credentials;
  /**
   * HTTP method to use for the initial request.
   *
   * **Optional parameter - defaults to GET if not specified.**
   * Existing code that doesn't provide this parameter will continue to work unchanged with standard GET requests.
   *
   * When specified with 'POST', 'PUT', or 'PATCH' methods that support a body,
   * you can also provide a `body` parameter with the request payload.
   *
   * **Platform Notes:**
   * - iOS: Full support for all HTTP methods with headers
   * - Android: Custom headers may not be sent with POST/PUT/PATCH requests due to WebView limitations
   *
   * @since 8.2.0
   * @default "GET"
   * @example
   * method: "POST",
   * body: JSON.stringify({ token: "auth-token", data: "value" }),
   * headers: { "Content-Type": "application/json" }
   */
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS' | string;
  /**
   * HTTP body to send with the request when using POST, PUT, or other methods that support a body.
   * Should be a string (use JSON.stringify for JSON data).
   *
   * **Optional parameter - only used when `method` is specified and supports a request body.**
   * Omitting this parameter (or using GET method) results in standard behavior without a request body.
   *
   * @since 8.2.0
   * @example
   * method: "POST",
   * body: JSON.stringify({ username: "user", password: "pass" }),
   * headers: { "Content-Type": "application/json" }
   */
  body?: string;
  /**
   * materialPicker: if true, uses Material Design theme for date and time pickers on Android.
   * This improves the appearance of HTML date inputs to use modern Material Design UI instead of the old style pickers.
   * @since 7.4.1
   * @default false
   * @example
   * materialPicker: true
   * Test URL: https://show-picker.glitch.me/demo.html
   */
  materialPicker?: boolean;
  /**
   * JavaScript Interface:
   * The webview automatically injects a JavaScript interface providing:
   * - `window.mobileApp.close()`: Closes the webview from JavaScript
   * - `window.mobileApp.postMessage(obj)`: Sends a message to the app (listen via "messageFromWebview" event)
   * - `window.mobileApp.hide()` / `window.mobileApp.show()` when allowWebViewJsVisibilityControl is true in CapacitorConfig
   * - `window.mobileApp.takeScreenshot()` when `allowScreenshotsFromWebPage` is true
   *
   * @example
   * // In your webpage loaded in the webview:
   * document.getElementById("closeBtn").addEventListener("click", () => {
   *   window.mobileApp.close();
   * });
   *
   * // Send data to the app
   * window.mobileApp.postMessage({ action: "login", data: { user: "test" }});
   *
   * @since 6.10.0
   */
  jsInterface?: never; // This property doesn't exist, it's just for documentation
  /**
   * Allows page JavaScript to call `window.mobileApp.takeScreenshot()`.
   * Disabled by default so only the host app can trigger native screenshots through the plugin API.
   *
   * @default false
   * @since 8.4.0
   */
  allowScreenshotsFromWebPage?: boolean;
  /**
   * Emits `consoleMessage` events for JavaScript `console.*` output coming from the managed page.
   * Useful when the webview stays hidden and you still need page-level diagnostics.
   *
   * @default false
   * @since 8.6.0
   */
  captureConsoleLogs?: boolean;
  /**
   * Automatically handles downloads triggered inside the webview without requiring a custom JavaScript bridge.
   *
   * When enabled:
   * - Standard attachment responses are written to a temporary file.
   * - `blob:` downloads are also captured when the platform supports them.
   * - Previewable files reopen inside the in-app browser when possible.
   * - Other files are handed off to the native preview or viewer flow.
   * - `downloadCompleted` and `downloadFailed` events notify the host app about the saved file.
   *
   * @default false
   * @since 8.6.0
   */
  handleDownloads?: boolean;
  /**
   * Share options for the webview. When provided, shows a disclaimer dialog before sharing content.
   * This is useful for:
   * - Warning users about sharing sensitive information
   * - Getting user consent before sharing
   * - Explaining what will be shared
   * - Complying with privacy regulations
   *
   * Note: shareSubject is required when using shareDisclaimer
   * @since 0.1.0
   * @example
   * shareDisclaimer: {
   *   title: "Disclaimer",
   *   message: "This is a test disclaimer",
   *   confirmBtn: "Accept",
   *   cancelBtn: "Decline"
   * }
   * Test URL: https://capgo.app
   */
  shareDisclaimer?: DisclaimerOptions;
  /**
   * Toolbar type determines the appearance and behavior of the browser's toolbar
   * - "activity": Shows a simple toolbar with just a close button and share button
   * - "navigation": Shows a full navigation toolbar with back/forward buttons
   * - "blank": Shows no toolbar
   * - "": Default toolbar with close button
   * @since 0.1.0
   * @default ToolBarType.DEFAULT
   * @example
   * toolbarType: ToolBarType.ACTIVITY,
   * title: "Activity Toolbar Test"
   * Test URL: https://capgo.app
   */
  toolbarType?: ToolBarType;
  /**
   * Subject text for sharing. Required when using shareDisclaimer.
   * This text will be used as the subject line when sharing content.
   * @since 0.1.0
   * @example "Share this page"
   */
  shareSubject?: string;
  /**
   * Title of the browser
   * @since 0.1.0
   * @default "New Window"
   * @example "Camera Test"
   */
  title?: string;
  /**
   * Background color of the browser
   * @since 0.1.0
   * @default BackgroundColor.BLACK
   */
  backgroundColor?: BackgroundColor;
  /**
   * If true, enables native navigation gestures within the webview.
   * - Android: Native back button navigates within webview history
   * - iOS: Enables swipe left/right gestures for back/forward navigation
   * @default false (Android), true (iOS - enabled by default)
   * @example
   * activeNativeNavigationForWebview: true,
   * disableGoBackOnNativeApplication: true
   * Test URL: https://capgo.app
   */
  activeNativeNavigationForWebview?: boolean;
  /**
   * Disable the possibility to go back on native application,
   * useful to force user to stay on the webview, Android only
   * @default false
   * @example
   * disableGoBackOnNativeApplication: true
   * Test URL: https://capgo.app
   */
  disableGoBackOnNativeApplication?: boolean;
  /**
   * Open url in a new window fullscreen
   * isPresentAfterPageLoad: if true, the browser will be presented after the page is loaded, if false, the browser will be presented immediately.
   * Promise timing: on Android, `openWebView()` resolves with the webview id when the webview is ready to be controlled
   * (immediately for hidden/immediate presentation, after the first page load when `isPresentAfterPageLoad` is `true`).
   * On iOS, the promise resolves with the id as soon as the native webview is created, even if presentation is deferred.
   * @since 0.1.0
   * @default false
   * @example
   * isPresentAfterPageLoad: true,
   * preShowScript: "await import('https://unpkg.com/darkreader@4.9.89/darkreader.js');\nDarkReader.enable({ brightness: 100, contrast: 90, sepia: 10 });"
   * Test URL: https://capgo.app
   */
  isPresentAfterPageLoad?: boolean;
  /**
   * Whether the website in the webview is inspectable or not, ios only
   * @default false
   */
  isInspectable?: boolean;
  /**
   * Whether the webview opening is animated or not, ios only
   * @default true
   */
  isAnimated?: boolean;
  /**
   * Shows a reload button that reloads the web page
   * @since 1.0.15
   * @default false
   * @example
   * showReloadButton: true
   * Test URL: https://capgo.app
   */
  showReloadButton?: boolean;
  /**
   * CloseModal: if true a confirm will be displayed when user clicks on close button, if false the browser will be closed immediately.
   * @since 1.1.0
   * @default false
   * @example
   * closeModal: true,
   * closeModalTitle: "Close Window",
   * closeModalDescription: "Are you sure you want to close?",
   * closeModalOk: "Yes, close",
   * closeModalCancel: "No, stay"
   * Test URL: https://capgo.app
   */
  closeModal?: boolean;
  /**
   * CloseModalTitle: title of the confirm when user clicks on close button
   * @since 1.1.0
   * @default "Close"
   */
  closeModalTitle?: string;
  /**
   * CloseModalDescription: description of the confirm when user clicks on close button
   * @since 1.1.0
   * @default "Are you sure you want to close this window?"
   */
  closeModalDescription?: string;
  /**
   * CloseModalOk: text of the confirm button when user clicks on close button
   * @since 1.1.0
   * @default "Close"
   */
  closeModalOk?: string;
  /**
   * CloseModalCancel: text of the cancel button when user clicks on close button
   * @since 1.1.0
   * @default "Cancel"
   */
  closeModalCancel?: string;
  /**
   * closeModalURLPattern: a regex pattern to match against the current URL when the close button is pressed.
   * When provided along with closeModal: true, the close confirmation modal is only shown if the current URL matches this pattern.
   * If the current URL does not match, the browser closes immediately without showing the modal.
   * Requires closeModal to be true.
   * @since 7.2.0
   * @example
   * closeModal: true,
   * closeModalURLPattern: ".*checkout.*"
   */
  closeModalURLPattern?: string;
  /**
   * visibleTitle: if true the website title would be shown else shown empty
   * @since 1.2.5
   * @default true
   */
  visibleTitle?: boolean;
  /**
   * toolbarColor: color of the toolbar in hex format
   * @since 1.2.5
   * @default "#ffffff"
   * @example
   * toolbarColor: "#FF5733"
   * Test URL: https://capgo.app
   */
  toolbarColor?: string;
  /**
   * toolbarTextColor: color of the buttons and title in the toolbar in hex format
   * When set, it overrides the automatic light/dark mode detection for text color
   * @since 6.10.0
   * @default calculated based on toolbarColor brightness
   * @example
   * toolbarTextColor: "#FFFFFF"
   * Test URL: https://capgo.app
   */
  toolbarTextColor?: string;
  /**
   * showArrow: if true an arrow would be shown instead of cross for closing the window
   * @since 1.2.5
   * @default false
   * @example
   * showArrow: true
   * Test URL: https://capgo.app
   */
  showArrow?: boolean;
  /**
   * ignoreUntrustedSSLError: if true, the webview will ignore untrusted SSL errors allowing the user to view the website.
   * @since 6.1.0
   * @default false
   */
  ignoreUntrustedSSLError?: boolean;
  /**
   * preShowScript: if isPresentAfterPageLoad is true and this variable is set the plugin will inject a script before showing the browser.
   * This script will be run in an async context. The plugin will wait for the script to finish (max 10 seconds)
   * @since 6.6.0
   * @example
   * preShowScript: "await import('https://unpkg.com/darkreader@4.9.89/darkreader.js');\nDarkReader.enable({ brightness: 100, contrast: 90, sepia: 10 });"
   * Test URL: https://capgo.app
   */
  preShowScript?: string;
  /**
   * preShowScriptInjectionTime: controls when the preShowScript is injected.
   * - "documentStart": injects before any page JavaScript runs (good for polyfills like Firebase)
   * - "pageLoad": injects after page load (default, original behavior)
   * @since 7.26.0
   * @default "pageLoad"
   * @example
   * preShowScriptInjectionTime: "documentStart"
   */
  preShowScriptInjectionTime?: 'documentStart' | 'pageLoad';
  /**
   * Proxy interception mode.
   *
   * - `true`: legacy blanket mode, delegates all HTTP/HTTPS requests to JavaScript.
   * - `string`: Android-only regex mode kept for backward compatibility.
   *
   * Prefer `outboundProxyRules` and `inboundProxyRules` for native-first matching.
   *
   * @since 6.9.0
   */
  proxyRequests?: boolean | string;
  /**
   * Native-first outbound proxy rules.
   *
   * @since 8.6.0
   */
  outboundProxyRules?: NativeProxyRule[];
  /**
   * Native-first inbound proxy rules.
   *
   * @since 8.6.0
   */
  inboundProxyRules?: NativeProxyRule[];
  /**
   * buttonNearDone allows for a creation of a custom button near the done/close button.
   * The button is only shown when toolbarType is not "activity", "navigation", or "blank".
   *
   * For Android:
   * - iconType must be "asset"
   * - icon path should be in the public folder (e.g. "monkey.svg")
   * - width and height are optional, defaults to 48dp
   * - button is positioned at the end of toolbar with 8dp margin
   *
   * For iOS:
   * - iconType can be "sf-symbol" or "asset"
   * - for sf-symbol, icon should be the symbol name
   * - for asset, icon should be the asset name
   * @since 6.7.0
   * @example
   * buttonNearDone: {
   *   ios: {
   *     iconType: "sf-symbol",
   *     icon: "star.fill"
   *   },
   *   android: {
   *     iconType: "asset",
   *     icon: "public/monkey.svg",
   *     width: 24,
   *     height: 24
   *   }
   * }
   * Test URL: https://capgo.app
   */
  buttonNearDone?: {
    ios: {
      iconType: 'sf-symbol' | 'asset';
      icon: string;
    };
    android: {
      iconType: 'asset' | 'vector';
      icon: string;
      width?: number;
      height?: number;
    };
  };
  /**
   * Shows a native screenshot button near the done/close button.
   * The button is hidden by default and captures the current viewport when tapped.
   * This option uses the same toolbar slot as `buttonNearDone` and is therefore incompatible with it.
   * The button is only shown when toolbarType is not "activity", "navigation", or "blank".
   *
   * @default false
   * @since 8.4.0
   */
  showScreenshotButton?: boolean;
  /**
   * textZoom: sets the text zoom of the page in percent.
   * Allows users to increase or decrease the text size for better readability.
   * @since 7.6.0
   * @default 100
   * @example
   * textZoom: 120
   * Test URL: https://capgo.app
   */
  textZoom?: number;
  /**
   * enableZoom: enables pinch-to-zoom gestures in the Android WebView.
   * When true, built-in zoom controls are enabled and the zoom buttons are hidden.
   * **Android only** — ignored on iOS where zoom is enabled by default.
   * @since 8.5.0
   * @default false
   * @example
   * enableZoom: true
   */
  enableZoom?: boolean;
  /**
   * preventDeeplink: if true, the deeplink will not be opened, if false the deeplink will be opened when clicked on the link. on IOS each schema need to be added to info.plist file under LSApplicationQueriesSchemes when false to make it work.
   * @since 0.1.0
   * @default false
   * @example
   * preventDeeplink: true
   * Test URL: https://aasa-tester.capgo.app/
   */
  preventDeeplink?: boolean;

  /**
   * When true, HTTP and HTTPS links opened from `target="_blank"` anchors stay in the current webview instead of spawning a popup or opening in the system browser.
   * By default, blank-target HTTP(S) links stay inside the plugin as managed popups that share cookies with the opener; enabling this option keeps everything in the same tab.
   * Custom schemes such as `tel:` and `mailto:` and authorized app links still prefer their native handlers unless `preventDeeplink` is enabled.
   *
   * @since 8.5.6
   * @default false
   * @example
   * openBlankTargetInWebView: true
   */
  openBlankTargetInWebView?: boolean;

  /**
   * List of base URLs whose hosts are treated as authorized App Links (Android) and Universal Links (iOS).
   *
   * - On both platforms, only HTTPS links whose host matches any entry in this list
   *   will attempt to open via the corresponding native application.
   * - If the app is not installed or the system cannot handle the link, the URL
   *   will continue loading inside the in-app browser.
   * - Matching is host-based (case-insensitive), ignoring the "www." prefix.
   * - When `preventDeeplink` is enabled, all external handling is blocked regardless of this list.
   *
   * @example
   * ```ts
   * ["https://example.com", "https://subdomain.app.io"]
   * ```
   *
   * @since 7.12.0
   * @default []
   */
  authorizedAppLinks?: string[];

  /**
   * If true, the webView will not take the full height and will have a 20px margin at the bottom.
   * This creates a safe margin area outside the browser view.
   * @since 7.13.0
   * @default false
   * @example
   * enabledSafeBottomMargin: true
   */
  enabledSafeBottomMargin?: boolean;

  /**
   * If false, the webView will extend behind the status bar for true full-screen immersive content.
   * When true (default), respects the safe area at the top of the screen.
   * Works independently of toolbarType - use for full-screen video players, games, or immersive web apps.
   * @since 8.2.0
   * @default true
   * @example
   * enabledSafeTopMargin: false  // Full screen, extends behind status bar
   */
  enabledSafeTopMargin?: boolean;

  /**
   * When true, applies the system status bar inset as the WebView top margin on Android.
   * Keeps the legacy 0px margin by default for apps that handle padding themselves.
   * @default false
   * @example
   * useTopInset: true
   */
  useTopInset?: boolean;

  /**
   * enableGooglePaySupport: if true, enables support for Google Pay popups and Payment Request API.
   * This fixes OR_BIBED_15 errors by allowing popup windows and configuring Cross-Origin-Opener-Policy.
   * Only enable this if you need Google Pay functionality as it allows popup windows.
   *
   * When enabled:
   * - Allows popup windows for Google Pay authentication
   * - Sets proper CORS headers for Payment Request API
   * - Enables multiple window support in WebView
   * - Configures secure context for payment processing
   *
   * @since 7.13.0
   * @default false
   * @example
   * enableGooglePaySupport: true
   * Test URL: https://developers.google.com/pay/api/web/guides/tutorial
   */
  enableGooglePaySupport?: boolean;

  /**
   * Opens popup windows created by the page in hidden mode.
   * Hidden popup windows can still be controlled with `executeScript`, `postMessage`, `show`, and `close`.
   * Listen to `popupWindowOpened` to capture the popup id, then call `show({ id })` only if you want to reveal it.
   *
   * @default false
   * @since 8.6.0
   * @example
   * hiddenPopupWindow: true
   */
  hiddenPopupWindow?: boolean;

  /**
   * blockedHosts: List of host patterns that should be blocked from loading in the InAppBrowser's internal navigations.
   * Any request inside WebView to a URL with a host matching any of these patterns will be blocked.
   * Supports wildcard patterns like:
   * - "*.example.com" to block all subdomains
   * - "www.example.*" to block wildcard domain extensions
   *
   * @since 7.17.0
   * @default []
   * @example
   * blockedHosts: ["*.tracking.com", "ads.example.com"]
   */
  blockedHosts?: string[];

  /**
   * Width of the webview in pixels.
   * If not set, webview will be fullscreen width.
   * @default undefined (fullscreen)
   * @example
   * width: 400
   */
  width?: number;

  /**
   * Height of the webview in pixels.
   * If not set, webview will be fullscreen height.
   * @default undefined (fullscreen)
   * @example
   * height: 600
   */
  height?: number;

  /**
   * X position of the webview in pixels from the left edge.
   * Only effective when width is set.
   * @default 0
   * @example
   * x: 50
   */
  x?: number;

  /**
   * Y position of the webview in pixels from the top edge.
   * Only effective when height is set.
   * @default 0
   * @example
   * y: 100
   */
  y?: number;

  /**
   * Disables the bounce (overscroll) effect on iOS WebView.
   * When enabled, prevents the rubber band scrolling effect when users scroll beyond content boundaries.
   * This is useful for:
   * - Creating a more native, app-like experience
   * - Preventing accidental overscroll states
   * - Avoiding issues when keyboard opens/closes
   *
   * Note: This option only affects iOS. Android does not have this bounce effect by default.
   *
   * @since 8.0.2
   * @default false
   * @example
   * disableOverscroll: true
   */
  disableOverscroll?: boolean;

  /**
   * Opens the webview in hidden mode (not visible to user but fully functional).
   * When hidden, the webview loads and executes JavaScript but is not displayed.
   * All control methods (executeScript, postMessage, setUrl, etc.) work while hidden.
   * Use close() to clean up the hidden webview when done.
   *
   * @since 8.0.7
   * @default false
   * @example
   * hidden: true
   */
  hidden?: boolean;

  /**
   * Controls how a hidden webview reports its visibility and size.
   * - AWARE: webview is aware it's hidden (dimensions may be zero).
   * - FAKE_VISIBLE: webview is hidden but reports fullscreen dimensions (uses alpha=0 to remain invisible).
   *
   * @default InvisibilityMode.AWARE
   * @example
   * invisibilityMode: InvisibilityMode.FAKE_VISIBLE
   */
  invisibilityMode?: InvisibilityMode;
}

export interface DimensionOptions {
  /**
   * Width of the webview in pixels
   */
  width?: number;
  /**
   * Height of the webview in pixels
   */
  height?: number;
  /**
   * X position from the left edge in pixels
   */
  x?: number;
  /**
   * Y position from the top edge in pixels
   */
  y?: number;
}

export interface InAppBrowserPlugin {
  /**
   * Navigates back in the WebView's history if possible
   *
   * @since 7.21.0
   * @returns Promise that resolves with true if navigation was possible, false otherwise
   */
  goBack(options?: { id?: string }): Promise<{ canGoBack: boolean }>;

  /**
   * Open url in a new window fullscreen, on android it use chrome custom tabs, on ios it use SFSafariViewController
   *
   * @since 0.1.0
   */
  open(options: OpenOptions): Promise<any>;

  /**
   * Clear cookies of url
   * When `id` is omitted, applies to all open webviews.
   *
   * @since 0.5.0
   */
  clearCookies(options: ClearCookieOptions): Promise<any>;
  /**
   * Clear all cookies
   * When `id` is omitted, applies to all open webviews.
   *
   * @since 6.5.0
   */
  clearAllCookies(options?: { id?: string }): Promise<any>;

  /**
   * Clear cache
   * When `id` is omitted, applies to all open webviews.
   *
   * @since 6.5.0
   */
  clearCache(options?: { id?: string }): Promise<any>;

  /**
   * Get cookies for a specific URL.
   * @param options The options, including the URL to get cookies for.
   * @returns A promise that resolves with the cookies.
   */
  getCookies(options: GetCookieOptions): Promise<Record<string, string>>;
  /**
   * Close the webview.
   * When `id` is omitted, closes the active webview.
   */
  close(options?: CloseWebviewOptions): Promise<any>;
  /**
   * Hide the webview without closing it.
   * Use show() to bring it back.
   * When `id` is omitted, targets the active webview.
   *
   * @since 8.0.8
   */
  hide(options?: { id?: string }): Promise<void>;
  /**
   * Show a previously hidden webview.
   * When `id` is omitted, targets the active webview.
   *
   * @since 8.0.8
   */
  show(options?: { id?: string }): Promise<void>;
  /**
   * Open url in a new webview with toolbars, and enhanced capabilities, like camera access, file access, listen events, inject javascript, bi directional communication, etc.
   *
   * JavaScript Interface:
   * When you open a webview with this method, a JavaScript interface is automatically injected that provides:
   * - `window.mobileApp.close()`: Closes the webview from JavaScript
   * - `window.mobileApp.postMessage({detail: {message: "myMessage"}})`: Sends a message from the webview to the app, detail object is the data you want to send to the webview
   * - `window.mobileApp.takeScreenshot()` when `allowScreenshotsFromWebPage` is true
   *
   * Promise timing differs by platform when `isPresentAfterPageLoad` is used.
   * Android resolves with `{ id }` after the dialog is ready to control, while iOS resolves with `{ id }` immediately after creating the native webview.
   *
   * @returns Promise that resolves with the created webview id.
   * @since 0.1.0
   */
  openWebView(options: OpenWebViewOptions): Promise<{ id: string }>;
  /**
   * Injects JavaScript code into the InAppBrowser window.
   * When `id` is omitted, executes in all open webviews.
   */
  executeScript(options: { code: string; id?: string }): Promise<void>;
  /**
   * Sends an event to the webview(inappbrowser). you can listen to this event in the inappbrowser JS with window.addEventListener("messageFromNative", listenerFunc: (event: Record<string, any>) => void)
   * detail is the data you want to send to the webview, it's a requirement of Capacitor we cannot send direct objects
   * Your object has to be serializable to JSON, so no functions or other non-JSON-serializable types are allowed.
   * When `id` is omitted, broadcasts to all open webviews.
   */
  postMessage(options: { detail: Record<string, any>; id?: string }): Promise<void>;
  /**
   * Captures the current webview viewport as a PNG screenshot.
   * When `id` is omitted, targets the active webview.
   */
  takeScreenshot(options?: { id?: string }): Promise<ScreenshotResult>;
  /**
   * Sets the URL of the webview.
   * When `id` is omitted, targets the active webview.
   */
  setUrl(options: { url: string; id?: string }): Promise<any>;
  /**
   * Listen for url change, only for openWebView
   *
   * @since 0.0.1
   */
  addListener(eventName: 'urlChangeEvent', listenerFunc: UrlChangeListener): Promise<PluginListenerHandle>;

  addListener(eventName: 'buttonNearDoneClick', listenerFunc: ButtonNearListener): Promise<PluginListenerHandle>;

  /**
   * Listen for close click only for openWebView
   *
   * @since 0.4.0
   */
  addListener(eventName: 'closeEvent', listenerFunc: UrlChangeListener): Promise<PluginListenerHandle>;
  /**
   * Will be triggered when user clicks on confirm button when disclaimer is required,
   * works with openWebView shareDisclaimer and closeModal
   *
   * @since 0.0.1
   */
  addListener(eventName: 'confirmBtnClicked', listenerFunc: ConfirmBtnListener): Promise<PluginListenerHandle>;
  /**
   * Will be triggered when event is sent from webview(inappbrowser), to send an event to the main app use window.mobileApp.postMessage({ "detail": { "message": "myMessage" } })
   * detail is the data you want to send to the main app, it's a requirement of Capacitor we cannot send direct objects
   * Your object has to be serializable to JSON, no functions or other non-JSON-serializable types are allowed.
   *
   * This method is inject at runtime in the webview
   */
  addListener(
    eventName: 'messageFromWebview',
    listenerFunc: (event: { id?: string; detail?: Record<string, any>; rawMessage?: string }) => void,
  ): Promise<PluginListenerHandle>;

  /**
   * Will be triggered whenever a screenshot is captured from the plugin API,
   * the native screenshot button, or the injected JavaScript bridge.
   */
  addListener(
    eventName: 'screenshotTaken',
    listenerFunc: (event: ScreenshotResult & { id?: string }) => void,
  ): Promise<PluginListenerHandle>;

  /**
   * Will be triggered when page is loaded
   */
  addListener(
    eventName: 'browserPageLoaded',
    listenerFunc: (event: { id?: string }) => void,
  ): Promise<PluginListenerHandle>;

  /**
   * Will be triggered when page load error
   */
  addListener(
    eventName: 'pageLoadError',
    listenerFunc: (event: { id?: string }) => void,
  ): Promise<PluginListenerHandle>;
  /**
   * Will be triggered after native download handling saves a file locally.
   * Enable this with `handleDownloads: true` when opening the webview.
   *
   * @since 8.6.0
   */
  addListener(
    eventName: 'downloadCompleted',
    listenerFunc: (event: DownloadCompletedEvent) => void,
  ): Promise<PluginListenerHandle>;
  /**
   * Will be triggered when native download handling fails.
   * Enable this with `handleDownloads: true` when opening the webview.
   *
   * @since 8.6.0
   */
  addListener(
    eventName: 'downloadFailed',
    listenerFunc: (event: DownloadFailedEvent) => void,
  ): Promise<PluginListenerHandle>;
  /**
   * Will be triggered whenever a page opens a popup/new window.
   * Use the returned popup id with `executeScript`, `postMessage`, `show`, `hide`, and `close`.
   *
   * @since 8.6.0
   */
  addListener(
    eventName: 'popupWindowOpened',
    listenerFunc: (event: PopupWindowEvent) => void,
  ): Promise<PluginListenerHandle>;
  /**
   * Listen for proxied requests delegated by the native runtime.
   * Prefer `addProxyHandler()` instead of calling this directly.
   *
   * @since 8.6.0
   */
  addListener(eventName: 'proxyRequest', listenerFunc: (event: ProxyRequest) => void): Promise<PluginListenerHandle>;
  /**
   * Listen for JavaScript console output emitted by the managed page.
   * Enable this with `captureConsoleLogs: true` when opening the webview.
   *
   * @since 8.6.0
   */
  addListener(
    eventName: 'consoleMessage',
    listenerFunc: (event: ConsoleMessageEvent) => void,
  ): Promise<PluginListenerHandle>;
  /**
   * Internal method used by `addProxyHandler()` to send a proxy decision back to native.
   * Forward the original `phase` when replying to a manual `proxyRequest` listener.
   *
   * @since 8.6.0
   */
  handleProxyRequest(options: {
    requestId: string;
    decision?: ProxyDecision | null;
    response?: ProxyResponse | null;
    webviewId?: string;
    phase?: 'outbound' | 'inbound';
  }): Promise<void>;
  /**
   * Remove all listeners for this plugin.
   *
   * @since 1.0.0
   */
  removeAllListeners(): Promise<void>;

  /**
   * Reload the current web page.
   *
   * @since 1.0.0
   */
  reload(options?: { id?: string }): Promise<any>;

  /**
   * Update the dimensions of the webview.
   * Allows changing the size and position of the webview at runtime.
   * When `id` is omitted, targets the active webview.
   *
   * @param options Dimension options (width, height, x, y)
   * @returns Promise that resolves when dimensions are updated
   */
  updateDimensions(options: DimensionOptions & { id?: string }): Promise<void>;

  /**
   * Sets the enabled safe top margin of the webview at runtime.
   * When `id` is omitted, targets the active webview.
   * On Web, this method is a no-op and resolves without changing layout.
   */
  setEnabledSafeTopMargin(options: { enabled: boolean; id?: string }): Promise<void>;

  /**
   * Sets the enabled safe bottom margin of the webview at runtime.
   * When `id` is omitted, targets the active webview.
   * On Web, this method is a no-op and resolves without changing layout.
   */
  setEnabledSafeBottomMargin(options: { enabled: boolean; id?: string }): Promise<void>;

  /**
   * Opens a secured window for OAuth2 authentication.
   * For web, you should have the code in the redirected page to use a broadcast channel to send the redirected url to the app
   * Something like:
   * ```html
   * <html>
   * <head></head>
   * <body>
   * <script>
   *   const searchParams = new URLSearchParams(location.search)
   *   if (searchParams.has("code")) {
   *     new BroadcastChannel("my-channel-name").postMessage(location.href);
   *     window.close();
   *   }
   * </script>
   * </body>
   * </html>
   * ```
   * For mobile, you should have a redirect uri that opens the app, something like: `myapp://oauth_callback/`
   * And make sure to register it in the app's info.plist:
   * ```xml
   * <key>CFBundleURLTypes</key>
   * <array>
   *    <dict>
   *       <key>CFBundleURLSchemes</key>
   *       <array>
   *          <string>myapp</string>
   *       </array>
   *    </dict>
   * </array>
   * ```
   * And in the AndroidManifest.xml file:
   * ```xml
   * <activity>
   *    <intent-filter>
   *       <action android:name="android.intent.action.VIEW" />
   *       <category android:name="android.intent.category.DEFAULT" />
   *       <category android:name="android.intent.category.BROWSABLE" />
   *       <data android:host="oauth_callback" android:scheme="myapp" />
   *    </intent-filter>
   * </activity>
   * ```
   * @param options - the options for the openSecureWindow call
   */
  openSecureWindow(options: OpenSecureWindowOptions): Promise<OpenSecureWindowResponse>;
}

/**
 * JavaScript APIs available in the InAppBrowser WebView.
 *
 * These APIs are automatically injected into all webpages loaded in the InAppBrowser WebView.
 *
 * @example
 * // Closing the webview from JavaScript
 * window.mobileApp.close();
 *
 * // Sending a message from webview to the native app
 * window.mobileApp.postMessage({ key: "value" });
 *
 * @since 6.10.0
 */
export interface InAppBrowserWebViewAPIs {
  /**
   * mobileApp - Global object injected into the WebView providing communication with the native app
   */
  mobileApp: {
    /**
     * Close the WebView from JavaScript
     *
     * @example
     * // Add a button to close the webview
     * const closeButton = document.createElement("button");
     * closeButton.textContent = "Close WebView";
     * closeButton.addEventListener("click", () => {
     *   window.mobileApp.close();
     * });
     * document.body.appendChild(closeButton);
     *
     * @since 6.10.0
     */
    close(): void;

    /**
     * Send a message from the WebView to the native app
     * The native app can listen for these messages with the "messageFromWebview" event
     *
     * @param message Object to send to the native app
     * @example
     * // Send data to native app
     * window.mobileApp.postMessage({
     *   action: "dataSubmitted",
     *   data: { username: "test", email: "test@example.com" }
     * });
     *
     * @since 6.10.0
     */
    postMessage(message: Record<string, any>): void;

    /**
     * Hide the WebView from JavaScript (requires allowWebViewJsVisibilityControl: true in CapacitorConfig)
     *
     * @since 8.0.8
     */
    hide(): void;

    /**
     * Show the WebView from JavaScript (requires allowWebViewJsVisibilityControl: true in CapacitorConfig)
     *
     * @since 8.0.8
     */
    show(): void;

    /**
     * Capture the current WebView viewport as a PNG screenshot.
     * Requires `allowScreenshotsFromWebPage` to be enabled when opening the webview.
     *
     * @since 8.4.0
     */
    takeScreenshot(): Promise<ScreenshotResult>;
  };

  /**
   * Get the native Capacitor plugin version
   *
   * @returns {Promise<{ id: string }>} an Promise with version for this device
   * @throws An error if the something went wrong
   */
  getPluginVersion(): Promise<{ version: string }>;
}
